import type { App } from 'vue';
import axios, { type AxiosInstance } from 'axios';
import type { Pinia } from 'pinia';
import { useSiteStore } from '@/stores/site';
import { useAdminAuthStore } from '@/stores/adminAuth';
import { useAppStore } from '@/stores/app';

const apiBaseUrl = import.meta.env.VITE_BACKEND_URL;

/** Variants that only serve the patient booking pages (no admin area). */
const PATIENT_ONLY_VARIANTS = ['magazin', 'network'];

export const api: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
});

export function installApi(app: App, pinia: Pinia) {
  app.config.globalProperties.$axios = axios;
  app.config.globalProperties.$api = api;

  api.interceptors.request.use((config) => {
    const adminAuth = useAdminAuthStore(pinia);
    adminAuth.hydrate();
    const token = adminAuth.getAuthorizationHeader();
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = token;
    }

    const siteStore = useSiteStore(pinia);
    const siteKey = siteStore.selectedSiteKey;
    if (siteKey) {
      config.headers = config.headers || {};
      config.headers['X-Site-Key'] = siteKey;
    }

    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      // A request we cancelled ourselves is not a failure. It carries no
      // `response`, so without this it would fall into the `!error.response`
      // branch below and blank the whole app behind the offline overlay every
      // time a caller supersedes its own in-flight request.
      if (axios.isCancel(error)) {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }

      const appStore = useAppStore(pinia);

      // Handle 401 Unauthorized -> Global Logout
      if (error.response?.status === 401) {
        const adminAuth = useAdminAuthStore(pinia);
        adminAuth.logout();
        // We don't force a page reload here to avoid infinite loops,
        // but the app state will change to "not authenticated"
      }

      // Handle 403 Forbidden -> redirect to admin-unauthorized if in admin layout
      if (error.response?.status === 403) {
        const { default: router } = await import('@/router');
        if (router.currentRoute.value.path.includes('/admin')) {
          const lang = router.currentRoute.value.params.lang || 'de';
          void router.push({ name: 'admin-unauthorized', params: { lang } });
          return Promise.reject(error instanceof Error ? error : new Error(String(error)));
        }
      }

      // Handle common status codes
      if (error.response?.status === 503) {
        const { useBookingAvailable } = await import('@/composables/useBookingAvailable');
        const { checkBookingAvailability } = useBookingAvailable();
        await checkBookingAvailability();
      } else if (
        (error.response?.status === 502 && !error.response?.data?.error_code) ||
        !error.response
      ) {
        // A raw 502 (no `error_code`) means the backend itself is unreachable.
        // An app-level ProviderError can also carry status 502 (e.g. a Lexware
        // sync failure) but always has `error_code` set - that's a real error
        // to show, not an outage, so it must fall through to the toast below.
        appStore.setOffline(true);
      } else {
        // General error notification
        const message =
          error.response?.data?.detail || error.message || 'An unexpected error occurred';
        // Only show toast if it's not a GET request (to avoid spamming on list views)
        // or if explicitly desired. For now, let's show it for everything that is not 401.
        // Not on the magazin/network patient pages: they report failures in their own
        // words (form messages, status page), the raw detail is meant for the admin area.
        if (
          error.response?.status !== 401 &&
          !error.config?.silentErrors &&
          !PATIENT_ONLY_VARIANTS.includes(import.meta.env.VITE_APP_VARIANT ?? '')
        ) {
          appStore.pushToast('error', message);
        }
      }

      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    },
  );
}

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Skip the error toast below - the caller reports the failure itself. */
    silentErrors?: boolean;
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $axios: typeof axios;
    $api: AxiosInstance;
  }
}
